/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import {
	BackgroundContainer,
	BlurredImage,
	CosmosComponentContextProps,
	CosmosText,
	Directory,
	extractBackgroundContainerProps,
	extractSectionContainerProps,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
	useCosmosComponentContext,
} from "@cosmos/components";
import { useAuth } from "@cosmos/next-hooks";
import { useMessages } from "@epic-mw/i18n";

import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

import { TemplateProviders } from "../../templates/TemplateProviders";

import { buildServicePartnerDetailView } from "./buildServicePartnerDetailView";
import { PartnerAttributeTable } from "./PartnerAttributeTable";
import { PartnerCaseStudies } from "./PartnerCaseStudies";
import { PartnerProfileHeader } from "./PartnerProfileHeader";

import type { ServicePartnerDetailData } from "./servicePartnerDetail";

interface Props {
	data: ServicePartnerDetailData;
	componentContext?: CosmosComponentContextProps;
}

const descStyles = css({
	transform: "translateZ(0)",
	"& .title-desc-stack .cosmos-text": { color: "eds.text.primary" },
});

const bgImageStyles = css({
	position: "absolute",
	pointerEvents: "none",
	top: 0,
	bottom: 0,
	right: 0,
	left: 0,
	width: "100%",
	aspectRatio: "16 / 9",
});

/**
 * Basic detail view for one service partner, reached at
 * `<directory path>/<partner slug>`. The detail chrome (`servicePartnerDetail`)
 * supplies the section config + blurred background image authored on the parent
 * directory page; `buildServicePartnerDetailView` shapes every label/data row.
 * Everything else is a straightforward profile layout (logo, description,
 * attribute rows, optional case study) plus a link back to the directory.
 */
export const ServicePartnerDetail = ({ data, componentContext }: Props): React.JSX.Element => {
	const contextFromProvider = useCosmosComponentContext();
	const ctx = componentContext ?? contextFromProvider;
	useAuth(!ctx.enableAuth);

	const messages = useMessages();
	const { partner, servicePartnerDetail } = data;
	const view = buildServicePartnerDetailView(partner, messages);

	const config = servicePartnerDetail?.config;
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const blurredBackgroundImage = servicePartnerDetail?.blurredBackgroundImage;

	return (
		<TemplateProviders componentContext={ctx}>
			<BackgroundContainer {...extractBackgroundContainerProps(config)} bgContain>
				<ResponsiveContainer>
					<SectionContainer {...extractSectionContainerProps(config)}>
						{blurredBackgroundImage ? (
							<div className={bgImageStyles}>
								<BlurredImage
									image={blurredBackgroundImage}
									width={1920}
									height={1080}
									opacity={0.27}
									disableAutoplay
									blurPixels={150}
								/>
							</div>
						) : null}
						{validHeading ? (
							<VStack alignItems="stretch" className={cx("service-partner-detail-heading", descStyles)}>
								<SectionHeading {...config?.heading} />
							</VStack>
						) : null}
						<VStack gap="eds.dimension.80" alignItems="start">
							<PartnerProfileHeader
								name={partner.name}
								logoUrl={partner.logoUrl}
								backHref={data.backHref}
								backLabel={view.backLabel}
								badge={view.badge}
								meta={view.meta}
								primaryTags={partner.industries}
								secondaryTags={partner.supportType}
								buttons={view.buttons}
							/>
							{partner.description ? (
								<CosmosText as="p" variant="paragraphMD" color="eds.text.secondary" text={partner.description} />
							) : null}
						</VStack>
					</SectionContainer>
				</ResponsiveContainer>
			</BackgroundContainer>

			{view.caseStudySection ? <PartnerCaseStudies section={view.caseStudySection} /> : null}

			<Directory.Section disablePaddingBlockStart={!view.caseStudySection}>
				<VStack gap="eds.dimension.80" alignItems="start">
					<PartnerAttributeTable rows={view.rows} />
				</VStack>
			</Directory.Section>
		</TemplateProviders>
	);
};
